import clsx from "clsx";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import {
  ContentContainer,
  ContentHeader,
  ContentScroll,
} from "features/layout/components";

import RenderIf from "common/components/RenderIf";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "common/components/ui/Breadcrumb";
import Skeleton from "common/components/ui/Skeleton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useLeadList from "../../datahooks/useLeadList";
import AddLeadsButton from "./AddLeadsButton";
import LeadListHeading from "./LeadListHeading";
import LeadListSources from "./LeadListSources";
import LeadListSourcesSkeleton from "./LeadListSourcesSkeleton";

export default function LeadListDetails() {
  const isDesktop = useTwBreakpoint("lg");

  const { listId } = useParams();
  const { state } = useLocation();
  const { leadList, isLoadingLeadList } = useLeadList({
    listId,
    placeholderList: state?.leadList,
  });

  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const selectSource = (sourceId: string) =>
    setSelectedSourceId((current) => (current === sourceId ? null : sourceId));

  const addLeadsButton = isLoadingLeadList ? (
    <Skeleton
      className={clsx("w-24 rounded-full", isDesktop ? "h-8" : "h-10")}
    />
  ) : (
    <AddLeadsButton listId={listId} />
  );

  return (
    <ContentContainer>
      <RenderIf condition={isDesktop}>
        <ContentHeader>
          <Breadcrumb className="overflow-hidden">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="..">Lead Lists</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbSeparator />

              <BreadcrumbItem>
                <BreadcrumbPage>Details</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {addLeadsButton}
        </ContentHeader>
      </RenderIf>

      <ContentScroll
        className={clsx(["flex flex-col gap-4 p-4", !isDesktop && "py-6"])}
      >
        <LeadListHeading leadList={leadList} />

        <RenderIf condition={!isDesktop}>
          <div className="flex flex-row gap-2 justify-between items-center">
            {addLeadsButton}

            {/* TODO: Add export leads button later on */}
          </div>
        </RenderIf>

        {leadList ? (
          <RenderIf condition={leadList.sources.length > 0}>
            <LeadListSources
              listId={leadList.id}
              sources={leadList.sources}
              selectedSourceId={selectedSourceId}
              selectSource={selectSource}
            />
          </RenderIf>
        ) : (
          <LeadListSourcesSkeleton />
        )}
      </ContentScroll>
    </ContentContainer>
  );
}
